import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AuthContext } from "../../contexts/AuthContext";
import Notification from "../../components/UI/Notification";
import UserSelector from "../Users/UserSelector";
import {
  FaChevronDown,
  FaChevronRight,
  FaSave,
  FaCheckSquare,
  FaRegSquare,
  FaMinusSquare,
  FaSearch,
} from "react-icons/fa";

const PERM_TYPES = {
  CREATE: "create",
  READ: "read",
  UPDATE: "update",
  DELETE: "delete",
};

// Data structure mirroring the application sidebar and features
const APP_MODULES = [
  {
    id: "general",
    title: "General",
    features: [
      { id: "overview", name: "Overview Dashboard", perms: [PERM_TYPES.READ] },
      {
        id: "onboarding",
        name: "On-boarding Form Response",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
    ],
  },
  {
    id: "users",
    title: "Users Management",
    features: [
      {
        id: "all_users",
        name: "All Users",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "emergency_contacts",
        name: "Emergency Contacts",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
    ],
  },
  {
    id: "attendance_management",
    title: "Attendance Management",
    features: [
      // Leave Management sub-tab
      {
        id: "leave_requests_apply",
        name: "Apply Leave",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "od_requests",
        name: "OD (Official Duty)",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "coplus_requests",
        name: "CO+ (Compensatory Off)",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "check_leave_balances_team",
        name: "Check Team Leave Balances",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "my_leave_balance",
        name: "My Leave Balance",
        perms: [PERM_TYPES.READ],
      },
      { id: "my_calendar", name: "My Calendar", perms: [PERM_TYPES.READ] },
      { id: "my_approvers", name: "My Approvers", perms: [PERM_TYPES.READ] },

      // Attendance sub-tab
      {
        id: "daily_attendance",
        name: "Daily Attendance",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "check_attendance_team",
        name: "Check Team Attendance",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "upload_attendance",
        name: "Upload Attendance (Bulk)",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "clock_in_out",
        name: "Clock-In/Out",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },

      // Requests sub-tab
      {
        id: "swipe_requests",
        name: "Swipe Requests",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "sto_requests",
        name: "Short Time Off (STO)",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "shift_change_requests",
        name: "Shift Change",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },

      // Approvals sub-tab
      {
        id: "approve_checkin_out",
        name: "Approve Check-In/Out",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_swipe",
        name: "Approve Swipe",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_coplus",
        name: "Approve CO+",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_od",
        name: "Approve OD",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_sto",
        name: "Approve STO",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_shift_change",
        name: "Approve Shift Change",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_leave",
        name: "Approve Leave Requests",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
    ],
  },
  {
    id: "payroll",
    title: "Payroll Management",
    features: [
      {
        id: "payroll_config",
        name: "Payroll Configuration",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "salary_config",
        name: "Salary Configuration",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "payroll_settings",
        name: "Payroll Settings",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "component_types",
        name: "Component Types",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "tax_config",
        name: "Tax Configurations",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },

      {
        id: "employee_mgmt",
        name: "Employee Management",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "salary_structures",
        name: "Salary Structures",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "employee_salary",
        name: "Employee Salary",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "salary_history",
        name: "Salary History",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "salary_fitment",
        name: "Salary Fitment",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "bulk_update",
        name: "Bulk Update",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "import_export",
        name: "Import/Export Data",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },

      { id: "payroll_list", name: "Payroll List", perms: [PERM_TYPES.READ] },
      {
        id: "generate_payroll",
        name: "Generate Payroll",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "pending_approvals",
        name: "Pending Approvals",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approval_history",
        name: "Approval History",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "reports_mgmt",
        name: "Reports Management",
        perms: [PERM_TYPES.READ],
      },
    ],
  },
  {
    id: "recruitment",
    title: "Recruitment",
    features: [
      {
        id: "recruitment_dashboard",
        name: "Dashboard",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "hiring_request",
        name: "Hiring Request",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "hod_requests",
        name: "HOD Requests",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "application_responses",
        name: "Application Responses",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "campaigns",
        name: "Campaigns",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "all_candidates",
        name: "All Candidates",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "interviews",
        name: "Interviews",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "resignations",
        name: "Resignations",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
    ],
  },
  {
    id: "file_management",
    title: "File Management",
    features: [
      {
        id: "upload_resumes",
        name: "Upload Resumes",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "manage_files",
        name: "Manage Files",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ, PERM_TYPES.DELETE],
      },
    ],
  },
  {
    id: "settings",
    title: "Settings",
    features: [
      {
        id: "role_settings",
        name: "Roles Configuration",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "permission_settings",
        name: "Permissions Assignment",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
    ],
  },
  {
    id: "master",
    title: "Master Data",
    features: [
      {
        id: "master_employment_type",
        name: "Employment Type",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_departments",
        name: "Departments",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_designations",
        name: "Designations",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_branch",
        name: "Branch/Workplace",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_state_code",
        name: "State Code",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_employee_grade",
        name: "Employee Grade",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_categories",
        name: "Categories",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_salary_grade",
        name: "Salary Grade",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_leave_type",
        name: "Leave Type",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_shifts",
        name: "Shifts",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_holidays",
        name: "Holidays",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_leave_balance",
        name: "Leave Balance Master",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_forms",
        name: "Forms Master",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_dept_hods",
        name: "Department HODs",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_approver_mod",
        name: "Approver Modules",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_approval_lvl",
        name: "Approval Level",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_emp_approver",
        name: "Employee Approver",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_leave_policy",
        name: "Leave Policy",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_salary_comp",
        name: "Salary Component",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
    ],
  },
  {
    id: "policy",
    label: "Company Policy",
    icon: "📄",
    features: [
      {
        id: "policy_mgmt",
        name: "Policy Management",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
    ],
  },
];

const AssignPermissions = () => {
  const { token } = useContext(AuthContext);
  const [notification, setNotification] = useState(null);

  const [formData, setFormData] = useState({ id: "" });
  const [permissionsVisible, setPermissionsVisible] = useState(false);

  const [permissions, setPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [permissionsViaRoles, setPermissionsViaRoles] = useState([]);

  const [expandedModules, setExpandedModules] = useState(
    APP_MODULES.reduce((acc, mod) => ({ ...acc, [mod.id]: true }), {}),
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchPermissionsForUser = async () => {
    if (!formData.id) {
      setNotification({
        type: "error",
        message: "Please select a user first.",
      });
      return;
    }

    setIsLoading(true);
    setNotification(null);
    try {
      const response = await axios.get(
        `https://hrmspayroll.prepstripe.com/api/users/${formData.id}/permissions`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data?.success) {
        const allPerms = response.data.data.all_permissions || [];
        const viaRoles = response.data.data.permissions_via_roles || [];

        setOriginalPermissions(allPerms);
        setPermissionsViaRoles(viaRoles);

        // Convert array of strings to flat object state
        const initialPerms = {};
        allPerms.forEach((p) => {
          initialPerms[p] = true;
        });
        setPermissions(initialPerms);
        setPermissionsVisible(true);
        setNotification({
          type: "success",
          message: response.data.message || "Permissions loaded successfully.",
        });
      } else {
        setNotification({
          type: "error",
          message: response.data?.message || "Failed to fetch permissions.",
        });
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Error fetching permissions.";
      setNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!formData.id) return;

    // Calculate what to add and what to remove
    const currentPermKeys = Object.keys(permissions).filter(
      (k) => permissions[k],
    );

    const toAdd = currentPermKeys.filter(
      (p) => !originalPermissions.includes(p),
    );
    // Things that were in originalPermissions and are NO LONGER in currentPermKeys
    const toRemove = originalPermissions.filter(
      (p) => !currentPermKeys.includes(p),
    );

    // Warn if trying to remove permissions mapped via role (the API might fail or ignore it)
    const removedRolePerms = toRemove.filter((p) =>
      permissionsViaRoles.includes(p),
    );
    if (removedRolePerms.length > 0) {
      console.warn(
        "Attempting to remove role-inherited permissions directly:",
        removedRolePerms,
      );
      // We can proceed, the backend should handle it or ignore it.
    }

    setIsSaving(true);
    setNotification(null);
    try {
      const response = await axios.post(
        `https://hrmspayroll.prepstripe.com/api/users/${formData.id}/permissions`,
        {
          add: toAdd,
          remove: toRemove,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data?.success) {
        setNotification({
          type: "success",
          message: response.data.message || "Permissions updated successfully.",
        });

        // Reset state after successful save
        setFormData({ id: "" });
        setPermissionsVisible(false);
        setPermissions({});
        setOriginalPermissions([]);
        setPermissionsViaRoles([]);
      } else {
        setNotification({
          type: "error",
          message: response.data?.message || "Failed to update permissions.",
        });
      }
    } catch (error) {
      console.error("Error saving permissions:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Error saving permissions.";
      setNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleModule = (modId) => {
    setExpandedModules((prev) => ({ ...prev, [modId]: !prev[modId] }));
  };

  const handlePermChange = (featureId, permType, isChecked) => {
    const key = `${featureId}_${permType}`;
    // Optionally prevent unchecking if it's via a role?
    // Let's just allow it, and the backend handles resolution, or we show a message later.
    setPermissions((prev) => ({
      ...prev,
      [key]: isChecked,
    }));
  };

  const isPermChecked = (featureId, permType) => {
    return !!permissions[`${featureId}_${permType}`];
  };

  const isPermViaRole = (featureId, permType) => {
    return permissionsViaRoles.includes(`${featureId}_${permType}`);
  };

  const getFeatureSelectionState = (feature) => {
    let checkedCount = 0;
    feature.perms.forEach((p) => {
      if (isPermChecked(feature.id, p)) checkedCount++;
    });

    if (checkedCount === 0) return "none";
    if (checkedCount === feature.perms.length) return "all";
    return "partial";
  };

  const handleFeatureToggleAll = (feature) => {
    const currentState = getFeatureSelectionState(feature);
    const newValue = currentState !== "all";

    const newPerms = { ...permissions };
    feature.perms.forEach((p) => {
      newPerms[`${feature.id}_${p}`] = newValue;
    });
    setPermissions(newPerms);
  };

  const getModuleSelectionState = (moduleObj) => {
    let totalPerms = 0;
    let checkedCount = 0;

    moduleObj.features.forEach((feat) => {
      feat.perms.forEach((p) => {
        totalPerms++;
        if (isPermChecked(feat.id, p)) checkedCount++;
      });
    });

    if (checkedCount === 0) return "none";
    if (checkedCount === totalPerms) return "all";
    return "partial";
  };

  const handleModuleToggleAll = (moduleObj) => {
    const currentState = getModuleSelectionState(moduleObj);
    const newValue = currentState !== "all";

    const newPerms = { ...permissions };
    moduleObj.features.forEach((feat) => {
      feat.perms.forEach((p) => {
        newPerms[`${feat.id}_${p}`] = newValue;
      });
    });
    setPermissions(newPerms);
  };

  const getPageSelectionState = () => {
    let totalPerms = 0;
    let checkedCount = 0;
    APP_MODULES.forEach((mod) => {
      mod.features.forEach((feat) => {
        feat.perms.forEach((p) => {
          totalPerms++;
          if (isPermChecked(feat.id, p)) checkedCount++;
        });
      });
    });
    if (checkedCount === 0) return "none";
    if (checkedCount === totalPerms) return "all";
    return "partial";
  };

  const handleToggleAllOnPage = () => {
    const currentState = getPageSelectionState();
    const newValue = currentState !== "all";
    const newPerms = { ...permissions };
    APP_MODULES.forEach((mod) => {
      mod.features.forEach((feat) => {
        feat.perms.forEach((p) => {
          newPerms[`${feat.id}_${p}`] = newValue;
        });
      });
    });
    setPermissions(newPerms);
  };

  const renderCheckboxOrDash = (feature, permType) => {
    if (!feature.perms.includes(permType)) {
      return (
        <span className="text-gray-300 dark:text-gray-600 block text-center">
          -
        </span>
      );
    }
    const viaRole = isPermViaRole(feature.id, permType);
    return (
      <div
        className="flex justify-center"
        title={viaRole ? "Granted via Role" : ""}
      >
        <input
          type="checkbox"
          className={`w-4 h-4 rounded border-gray-300 focus:ring-blue-500 cursor-pointer ${viaRole ? "text-purple-600 focus:ring-purple-500" : "text-blue-600"}`}
          checked={isPermChecked(feature.id, permType)}
          onChange={(e) =>
            handlePermChange(feature.id, permType, e.target.checked)
          }
        />
      </div>
    );
  };

  const getIconForState = (state) => {
    if (state === "all")
      return <FaCheckSquare className="text-blue-600 w-5 h-5 mx-auto" />;
    if (state === "partial")
      return (
        <FaMinusSquare className="text-blue-600 w-5 h-5 opacity-70 mx-auto" />
      );
    return <FaRegSquare className="text-gray-400 w-5 h-5 mx-auto" />;
  };

  // Hide permissions block if user resets the selection
  useEffect(() => {
    if (!formData.id && permissionsVisible) {
      setPermissionsVisible(false);
      setPermissions({});
      setOriginalPermissions([]);
    }
  }, [formData.id, permissionsVisible]);

  return (
    <div className="w-full font-poppins p-4 md:p-6">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="w-full mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-800 dark:text-white font-poppins">
              Add Permission
            </h2>
          </div>
          <p className="text-gray-500 dark:text-gray-400 -mt-4 mb-6">
            Select a user to assign or remove direct system permissions.
          </p>

          <div className="py-6 border-t border-gray-100 dark:border-gray-800">
            <div className="flex flex-col md:flex-row items-end gap-4 max-w-3xl">
              <div className="w-full md:w-2/3">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
                  Select User <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <UserSelector formData={formData} setFormData={setFormData} />
                </div>
              </div>
              <div className="w-full md:w-1/3">
                <button
                  type="button"
                  onClick={fetchPermissionsForUser}
                  disabled={!formData.id || isLoading}
                  className="w-full px-6 py-[10.5px] bg-blue-600 text-white font-semibold rounded focus:ring-2 focus:ring-blue-500 hover:bg-blue-700 shadow flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? (
                    "Loading..."
                  ) : (
                    <>
                      <FaSearch /> Fetch Permissions
                    </>
                  )}
                </button>
              </div>
            </div>
            {permissionsVisible && (
              <div className="mt-4 flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-purple-600"></div>
                  <span>Granted via Role (Cannot remove directly)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-blue-600"></div>
                  <span>Direct Permission</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {permissionsVisible && (
          <div className="w-full transition-all duration-300">
            <div className="py-4 border-b border-gray-200 dark:border-gray-700 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                User Permissions
              </h2>
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-2 pb-3 border-b border-gray-300 dark:border-gray-600 font-semibold text-gray-600 dark:text-gray-400 text-sm uppercase tracking-wider mb-2">
                  <div className="col-span-4 pl-2">Module / Feature</div>
                  <div className="col-span-2 text-center border-l dark:border-gray-600">
                    <button
                      type="button"
                      onClick={handleToggleAllOnPage}
                      className="focus:outline-none flex items-center justify-center w-full gap-1"
                      title="Toggle all permissions on page"
                    >
                      {getIconForState(getPageSelectionState())}
                      <span className="text-[10px]">ALL</span>
                    </button>
                  </div>
                  <div className="col-span-1 text-center border-l dark:border-gray-600">
                    Read
                  </div>
                  <div className="col-span-2 text-center border-l dark:border-gray-600">
                    Create
                  </div>
                  <div className="col-span-1 text-center border-l dark:border-gray-600">
                    Update
                  </div>
                  <div className="col-span-2 text-center border-l dark:border-gray-600">
                    Delete
                  </div>
                </div>

                {/* Table Body */}
                <div className="space-y-4">
                  {APP_MODULES.map((mod) => (
                    <div
                      key={mod.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800"
                    >
                      <div className="grid grid-cols-12 gap-2 bg-gray-100 dark:bg-gray-900/50 p-3 items-center hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-colors">
                        <div
                          className="col-span-4 flex items-center gap-2 cursor-pointer font-semibold text-gray-800 dark:text-gray-200"
                          onClick={() => toggleModule(mod.id)}
                        >
                          {expandedModules[mod.id] ? (
                            <FaChevronDown size={14} />
                          ) : (
                            <FaChevronRight size={14} />
                          )}
                          {mod.title}
                        </div>
                        <div className="col-span-2 flex items-center justify-center border-l dark:border-gray-700 border-gray-300 pl-2">
                          <button
                            type="button"
                            onClick={() => handleModuleToggleAll(mod)}
                            className="focus:outline-none"
                          >
                            {getIconForState(getModuleSelectionState(mod))}
                          </button>
                        </div>
                        <div className="col-span-6"></div>
                      </div>

                      {expandedModules[mod.id] && (
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                          {mod.features.map((feat) => (
                            <div
                              key={feat.id}
                              className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors"
                            >
                              <div className="col-span-4 pl-8 text-sm font-medium text-gray-700 dark:text-gray-300">
                                {feat.name}
                              </div>
                              <div className="col-span-2 border-l border-gray-200 dark:border-gray-700">
                                <button
                                  type="button"
                                  onClick={() => handleFeatureToggleAll(feat)}
                                  className="focus:outline-none w-full flex justify-center"
                                >
                                  {getIconForState(
                                    getFeatureSelectionState(feat),
                                  )}
                                </button>
                              </div>
                              <div className="col-span-1 border-l border-gray-200 dark:border-gray-700">
                                {renderCheckboxOrDash(feat, PERM_TYPES.READ)}
                              </div>
                              <div className="col-span-2 border-l border-gray-200 dark:border-gray-700">
                                {renderCheckboxOrDash(feat, PERM_TYPES.CREATE)}
                              </div>
                              <div className="col-span-1 border-l border-gray-200 dark:border-gray-700">
                                {renderCheckboxOrDash(feat, PERM_TYPES.UPDATE)}
                              </div>
                              <div className="col-span-2 border-l border-gray-200 dark:border-gray-700 pl-4 pr-4">
                                {renderCheckboxOrDash(feat, PERM_TYPES.DELETE)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="py-5 flex items-center justify-end gap-3">
              <button
                onClick={() => setPermissionsVisible(false)}
                className="px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-medium transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSavePermissions}
                disabled={isSaving}
                className="px-6 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow flex items-center gap-2 font-medium disabled:opacity-50 transition-colors"
              >
                <FaSave /> {isSaving ? "Saving..." : "Save Assign Permission"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignPermissions;
